import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePageTitle } from '@/hooks/usePageTitle'
import {
  LayoutDashboard,
  Users,
  Building2,
  CreditCard,
  FileText,
  Package,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  ShieldCheck,
  Menu,
  X,
  ExternalLink,
  UserCircle2,
  StickyNote,
  Megaphone,
  MessageSquare,
  Library,
  Database,
  BarChart3,
} from 'lucide-react'

const NAV_ITEMS = [
  { path: '/', icon: LayoutDashboard, label: '대시보드', exact: true, permission: 'DASHBOARD' },
  { path: '/users', icon: Users, label: '사용자 관리', permission: 'USERS' },
  { path: '/organizations', icon: Building2, label: '기관 관리', permission: 'ORGANIZATIONS' },
  { path: '/subscriptions/org', icon: Building2, label: '조직 구독 관리', permission: 'SUBSCRIPTIONS_ORG' },
  { path: '/subscriptions/personal', icon: CreditCard, label: '개인 구독 관리', permission: 'SUBSCRIPTIONS_PERSONAL' },
  { path: '/products', icon: Package, label: '상품 관리', permission: 'PRODUCTS' },
  { path: '/contents', icon: FileText, label: '콘텐츠 관리', permission: 'CONTENTS' },
  { path: '/configurations', icon: Settings, label: '학습진행 설정', permission: 'CONFIGURATIONS' },
  { path: '/learning-status', icon: BarChart3, label: '학습 현황', permission: 'LEARNING_STATUS' },
  { path: '/announcements', icon: Megaphone, label: '공지사항 관리', permission: 'ANNOUNCEMENTS' },
  { path: '/support-inquiries', icon: MessageSquare, label: '문의 관리', permission: 'SUPPORT_INQUIRIES' },
  { path: '/library', icon: Library, label: '자료실', permission: 'LIBRARY' },
  { path: '/notes', icon: StickyNote, label: '메모장', permission: 'NOTES' },
  { path: '/data-stats', icon: Database, label: 'DATA 확인', permission: 'DATA_STATS' },
]

export function AdminLayout() {
  const { user, logout, hasPermission, isSuper } = useAuthStore()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // 페이지 타이틀 자동 업데이트
  usePageTitle()

  // 권한 기반 메뉴 필터링
  const visibleNavItems = NAV_ITEMS.filter(item => hasPermission(item.permission))

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path
    }
    
    // '/' 경로는 exact일 때만 매칭되도록 함
    if (path === '/') {
      return false
    }
    
    // 정확히 일치하거나 하위 경로인지 확인
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-amber-400" />
          <span className="font-bold text-lg">Quebon Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="http://localhost:3000"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-300 hover:text-white"
            title="메인 사이트로 이동"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 hover:bg-slate-800 rounded-lg"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full bg-slate-900 text-white transition-all duration-300 flex flex-col
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700 flex-shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-amber-400" />
              <span className="font-bold text-xl">Admin</span>
            </div>
          ) : (
            <Shield className="w-8 h-8 text-amber-400 mx-auto" />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex p-2 hover:bg-slate-800 rounded-lg ml-auto"
          >
            {sidebarOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>
        </div>

        {/* 외부 링크 - 아이콘만 한 줄로 */}
        <div className="px-4 py-3 border-b border-slate-700 flex-shrink-0">
          <div className={`flex items-center gap-2 ${sidebarOpen ? 'justify-start' : 'justify-center'}`}>
            <a
              href="http://localhost:3000"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              title="메인 사이트"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
            <a
              href="http://localhost:3002"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              title="Company Admin"
            >
              <Building2 className="w-5 h-5" />
            </a>
            <a
              href="http://localhost:3005"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
              title="LC Admin"
            >
              <Users className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {visibleNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                ${isActive(item.path, item.exact)
                  ? 'bg-amber-500 text-slate-900 font-medium'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }
              `}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </Link>
          ))}

          {/* 역할 관리 - 깨봉Admin 전용 */}
          {isSuper() && (
            <>
              <div className="pt-2 mt-2 border-t border-slate-700">
                {sidebarOpen && <p className="px-4 py-1 text-xs text-slate-500 uppercase">System</p>}
              </div>
              <Link
                to="/roles"
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                  ${isActive('/roles')
                    ? 'bg-amber-500 text-slate-900 font-medium'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }
                `}
              >
                <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>역할 관리</span>}
              </Link>
            </>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-slate-700 flex-shrink-0">
          {sidebarOpen ? (
            <div className="mb-3 px-2 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <UserCircle2 className="w-6 h-6 text-slate-900" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-400">관리자</p>
                <p className="font-medium truncate text-sm">{user?.email}</p>
              </div>
            </div>
          ) : (
            <div className="mb-3 flex justify-center">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center" title={user?.email}>
                <UserCircle2 className="w-6 h-6 text-slate-900" />
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg w-full text-slate-300 hover:bg-slate-800 hover:text-white transition-colors
              ${!sidebarOpen && 'justify-center px-2'}
            `}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span>로그아웃</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={`
          transition-all duration-300 pt-16 lg:pt-0
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  )
}

